import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from '../hooks/useTranslations';
import { Linkedin, Instagram } from 'lucide-react';
import { SiTiktok, SiReddit } from 'react-icons/si';
import { getLocalizedPath } from '../utils/localizedRoutes';
import { buildDubbingPairPath, DUBBING_LANGUAGE_PAIRS } from '../content/dubbingLanguagePairs';
import './Footer.css';

const splitIndex = Math.ceil(DUBBING_LANGUAGE_PAIRS.length / 2);
const languageDirectionsLeft = DUBBING_LANGUAGE_PAIRS.slice(0, splitIndex);
const languageDirectionsRight = DUBBING_LANGUAGE_PAIRS.slice(splitIndex);

export function Footer({ onShowBlog }: { onShowBlog?: () => void } = {}) {
  const { t, currentLanguage } = useTranslations();
  const privacyPath = getLocalizedPath('/privacy', currentLanguage);
  const termsPath = getLocalizedPath('/terms', currentLanguage);
  const blogPath = getLocalizedPath('/blog', currentLanguage);

  return (
    <footer className="vevi-footer">
      <div className="vevi-footer-container">
        <div className="vevi-footer-main">
          <section className="vevi-footer-languages" aria-label="Languages">
            <h3 className="vevi-footer-title">Languages</h3>
            <div
              className={`vevi-footer-languages-columns ${
                languageDirectionsRight.length === 0 ? 'vevi-footer-languages-columns-single' : ''
              }`}
            >
              <ul className="vevi-footer-list">
                {languageDirectionsLeft.map((item) => (
                  <li key={item.slug} className="vevi-footer-list-item">
                    <Link
                      to={getLocalizedPath(buildDubbingPairPath(item.slug), currentLanguage)}
                      className="vevi-footer-link"
                    >
                      {item.footerLabel}
                    </Link>
                  </li>
                ))}
              </ul>
              {languageDirectionsRight.length > 0 && (
                <ul className="vevi-footer-list">
                  {languageDirectionsRight.map((item) => (
                    <li key={item.slug} className="vevi-footer-list-item">
                      <Link
                        to={getLocalizedPath(buildDubbingPairPath(item.slug), currentLanguage)}
                        className="vevi-footer-link"
                      >
                        {item.footerLabel}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">About Service</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <Link to={privacyPath} className="vevi-footer-link">
                  {t.footer?.privacyPolicy || 'Privacy Policy'}
                </Link>
              </li>
              <li className="vevi-footer-list-item">
                <Link to={termsPath} className="vevi-footer-link">
                  {t.footer?.termsOfUse || 'Terms of Use'}
                </Link>
              </li>
              <li className="vevi-footer-list-item">
                <Link
                  to={blogPath}
                  className="vevi-footer-link"
                  onClick={() => onShowBlog?.()}
                >
                  {t.nav?.blog || 'Blog'}
                </Link>
              </li>
            </ul>
          </section>

          <section className="vevi-footer-column">
            <h3 className="vevi-footer-title">Social Media</h3>
            <ul className="vevi-footer-list">
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.linkedin.com/company/vevi-monster/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <Linkedin className="vevi-footer-social-icon" />
                  <span>LinkedIn</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.instagram.com/vevimonster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <Instagram className="vevi-footer-social-icon" />
                  <span>Instagram</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.tiktok.com/@vevi.monster"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <SiTiktok className="vevi-footer-social-icon" />
                  <span>TikTok</span>
                </a>
              </li>
              <li className="vevi-footer-list-item">
                <a
                  href="https://www.reddit.com/r/vevimonster/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vevi-footer-link vevi-footer-social-link"
                >
                  <SiReddit className="vevi-footer-social-icon" />
                  <span>Reddit</span>
                </a>
              </li>
            </ul>
          </section>
        </div>

        <div className="vevi-footer-bottom">
          <p className="vevi-footer-copyright">
            © 2026 VEVI Monster. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
